import {useState, useEffect, useRef} from 'react';
import ReactPaginate from 'react-paginate';
import classNames from 'classnames';
import {AiOutlineLeft, AiOutlineRight} from 'react-icons/ai';
import {ImArrowUp2, ImArrowDown2} from 'react-icons/im';

import TableRow from './TableRow';
import Loader from '../../ui/Loader';

const CustomTable = ({
  tHeadData,
  tBodyData,
  tbodyData,
  theadData,
  customClass = {root: ''},
  /* callback fn with updated [page, sortBy, sortDirection]  */
  handleSort = () => {},
  onSort,
  /* total number of pages for pagination */
  pageCount = null,
  totalItems,
  pageSize,
  currentPage,
  onPageChange,
  loading = false,
  isLoading = false,
}) => {
  // Normalize the props to handle different naming conventions
  const normalizedHeadData = theadData || tHeadData || [];
  const normalizedBodyData = tbodyData || tBodyData || [];
  const normalizedLoading = isLoading || loading;
  const normalizedSort = handleSort || onSort || (() => {});

  const [sortDirection, setSortDirection] = useState(null);
  const [sortBy, setSortBy] = useState(null);

  // Calculate pageCount if not provided but totalItems and pageSize are available
  const calculatedPageCount =
    totalItems && pageSize ? Math.ceil(totalItems / pageSize) : pageCount;

  // Only call sort when sortDirection or sortBy changes, not when normalizedSort changes
  useEffect(() => {
    normalizedSort({
      sortDirection,
      sortBy,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDirection, sortBy]);

  const mainBlockClass = 'w-10 h-10 flex-center select-none';
  const nextPreviousClasses = 'border-primary-main text-primary-main';

  return (
    <div>
      <table
        className={classNames('w-full relative', customClass.root, {
          'min-h-[200px]': normalizedLoading || !normalizedBodyData.length,
        })}
      >
        <thead>
          <tr className="bg-[#F5F5F5]">
            {normalizedHeadData.map(({id, keyIndex, label, sort = false}) => {
              return (
                <th
                  className="py-2 cursor-default"
                  key={id}
                  onClick={() => {
                    if (!sort) return;
                    if (sortDirection === 'desc') {
                      setSortBy(null);
                      setSortDirection(null);
                      return;
                    }
                    setSortDirection((prev) =>
                      prev === 'asc' ? 'desc' : 'asc',
                    );
                    setSortBy(keyIndex);
                  }}
                >
                  <div className="py-1 border-r text-start px-4 border-[#D9D9D9] relative select-none">
                    {label}
                    {keyIndex === sortBy && (
                      <div className="absolute right-2 top-2">
                        {sortDirection === 'asc' && <ImArrowUp2 size={18} />}
                        {sortDirection === 'desc' && <ImArrowDown2 size={18} />}
                      </div>
                    )}
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody className="relative">
          {normalizedLoading && (
            <tr className="z-10">
              <td className="absolute-center z-20">
                <Loader />
              </td>
            </tr>
          )}
          {!normalizedLoading && !normalizedBodyData.length && (
            <tr>
              <td className="absolute-center">No Data found</td>
            </tr>
          )}
          {!!normalizedBodyData.length &&
            normalizedBodyData.map((item, index) => {
              return (
                <TableRow
                  key={item._id || item.id || index}
                  data={item}
                  tHeadData={normalizedHeadData}
                  loading={normalizedLoading}
                />
              );
            })}
        </tbody>
      </table>

      {!!Number(calculatedPageCount) && (
        <div className="flex items-end justify-end px-2 pt-6">
          <ReactPaginate
            pageCount={calculatedPageCount}
            pageRangeDisplayed={3}
            marginPagesDisplayed={2}
            forcePage={currentPage ? currentPage - 1 : 0}
            containerClassName="flex-center gap-1"
            breakLinkClassName="w-10 h-10 flex-center select-none"
            activeLinkClassName="bg-primary-main text-white border border-primary-main"
            pageLinkClassName={mainBlockClass}
            nextLinkClassName={classNames(nextPreviousClasses, mainBlockClass)}
            previousLinkClassName={classNames(
              nextPreviousClasses,
              mainBlockClass,
            )}
            disabledLinkClassName="border-primary-gray !text-black bg-[#F5F5F5] cursor-not-allowed"
            previousLabel={<AiOutlineLeft size={18} />}
            nextLabel={<AiOutlineRight size={18} />}
            onPageChange={({selected}) => {
              const nextSelectedPage = selected + 1;
              if (onPageChange) {
                onPageChange(nextSelectedPage);
              } else {
                normalizedSort({
                  sortDirection,
                  sortBy,
                  page: nextSelectedPage,
                });
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

export default CustomTable;
